<template>
  <div class="qr-code-container">
    <div v-if="loading" class="flex justify-center items-center p-4">
      <div class="w-8 h-8 rounded-full border-b-2 animate-spin border-primary"></div>
    </div>
    <div v-else-if="error" class="p-4 text-center text-red-500">
      {{ error }}
    </div>
    <div v-else class="flex flex-col items-center">
      <img v-if="qrDataUrl" :src="qrDataUrl" :alt="alt || 'QR Code'" class="qr-code" />
      <div v-if="showDownload || showPrint" class="flex mt-3 space-x-2">
        <button
          v-if="showDownload"
          @click="downloadQR"
          class="inline-flex items-center px-4 py-2 text-xs font-semibold tracking-widest text-white uppercase bg-black rounded-md border border-transparent transition bg-primary hover:bg-primary-dark active:bg-primary-dark focus:outline-none focus:border-primary-dark focus:ring focus:ring-primary-light disabled:opacity-25"
        >
          Download QR Code
        </button>
        <button
          v-if="showPrint"
          @click="printQR"
          class="inline-flex items-center px-4 py-2 text-xs font-semibold tracking-widest text-white uppercase bg-gray-600 rounded-md border border-transparent transition hover:bg-gray-700 active:bg-gray-800 focus:outline-none focus:border-gray-700 focus:ring focus:ring-gray-300 disabled:opacity-25"
        >
          Print QR Code
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import QRCode from 'qrcode';
const props = defineProps({
  value: {
    type: String,
    required: true
  },
  size: {
    type: Number,
    default: 200
  },
  color: {
    type: String,
    default: '#000000'
  },
  backgroundColor: {
    type: String,
    default: '#ffffff'
  },
  errorCorrectionLevel: {
    type: String,
    default: 'M' // L, M, Q, H
  },
  margin: {
    type: Number,
    default: 4
  },
  alt: {
    type: String,
    default: ''
  },
  showDownload: {
    type: Boolean,
    default: true
  },
  showPrint: {
    type: Boolean,
    default: true
  },
  downloadName: {
    type: String,
    default: 'qrcode'
  },
  businessName: {
    type: String,
    default: ''
  }
});

const qrDataUrl = ref('');
const loading = ref(true);
const error = ref('');

const generateQR = async () => {
  if (!props.value) {
    error.value = 'No URL provided';
    loading.value = false;
    return;
  }

  try {
    loading.value = true;
    error.value = '';

    qrDataUrl.value = await QRCode.toDataURL(props.value, {
      width: props.size,
      margin: props.margin,
      color: {
        dark: props.color,
        light: props.backgroundColor
      },
      errorCorrectionLevel: props.errorCorrectionLevel
    });

    loading.value = false;
  } catch (err) {
    error.value = 'Failed to generate QR code';
    loading.value = false;
    console.error('QR Code generation error:', err);
  }
};

const downloadQR = () => {
  if (!qrDataUrl.value) return;

  const link = document.createElement('a');
  link.href = qrDataUrl.value;
  link.download = `${props.downloadName || 'business'}-qrcode.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const printQR = () => {
  if (!qrDataUrl.value) return;

  // Create a new window for printing
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Please allow pop-ups to print the QR code');
    return;
  }

  // Create the content for the print window
  const businessNameDisplay = props.businessName ?
    `<h2 style="font-family: Arial, sans-serif; font-size: 18px; margin-bottom: 10px;">${props.businessName}</h2>` : '';

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Business QR Code</title>
      <style>
        body {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100vh;
          margin: 0;
          padding: 20px;
          font-family: Arial, sans-serif;
        }
        .qr-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 20px;
          border: 1px solid #ccc;
          border-radius: 8px;
        }
        .qr-code {
          max-width: 100%;
          height: auto;
        }
        .instructions {
          margin-top: 15px;
          font-size: 16px;
          font-weight: bold;
          color: #666;
          text-align: center;
        }
        @media print {
          .no-print {
            display: none;
          }
        }
      </style>
    </head>
    <body>
      <div class="qr-container">
        ${businessNameDisplay}
        <img src="${qrDataUrl.value}" alt="Business QR Code" class="qr-code" style="width: ${props.size}px; height: ${props.size}px;" />
        <p class="instructions">Scan this code to visit our business page and order from us</p>
        <p class="instructions">${props.value}</p>
      </div>
      <div class="no-print" style="margin-top: 20px;">
        <button onclick="window.print(); setTimeout(() => window.close(), 500);" style="padding: 8px 16px; background-color: #4CAF50; color: white; border: none; border-radius: 4px; cursor: pointer;">
          Print
        </button>
      </div>
    </body>
    </html>
  `);

  printWindow.document.close();
};

onMounted(() => {
  generateQR();
});

watch(() => props.value, () => {
  generateQR();
});
</script>

<style scoped>
.qr-code-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.qr-code {
  max-width: 100%;
  height: auto;
}
</style>
